import { EventIds } from 'constants/eventIds';
import { useCallback, useRef } from 'react';
import { fetchWithCommonHeaders } from '@landr/core';
import { Label } from 'types/generated/samplesApi';
import { log } from 'utils/log';
import { CONFIG } from 'utils';
import { fetchErrorHandler } from 'helpers/fetchErrorHandler/fetchErrorHandler';

const ArtworkUrlGuidPlaceholder = 'GUID';

export type LabelSuggestionRequiredFields = Pick<
    Label,
    'artworkUrl' | 'slug' | 'name'
>;

let labels: LabelSuggestionRequiredFields[] = [];
let hasError = false;
let isLoading = false;
let loadLabelsPromise: Promise<Response> | undefined = undefined;

export const useLabels = () => {
    // Use AbortController to cancel requests when component unmounts
    const abortController = useRef<AbortController>();

    const load = useCallback(async () => {
        if (loadLabelsPromise) {
            return;
        }

        // Cancel any previous request
        if (abortController.current) {
            abortController.current.abort();
        }

        // Create new abort controller for this request
        abortController.current = new AbortController();

        try {
            isLoading = true;
            loadLabelsPromise = fetchWithCommonHeaders(
                `${CONFIG.VITE_API_URL}/labels/suggestions`,
                {
                    method: 'GET',
                    headers: {
                        accept: 'application/json',
                    },
                },
                {
                    log,
                    retryCount: 3,
                },
            );

            const response = await loadLabelsPromise;
            const data: { suggestions: Label[]; artworkUrl: string } =
                await response.json();

            const artworkUrlBase = data.artworkUrl;

            labels = data.suggestions.map((label) => ({
                ...label,
                artworkUrl: artworkUrlBase.replace(
                    ArtworkUrlGuidPlaceholder,
                    label.guid,
                ),
            }));
        } catch (error) {
            hasError = fetchErrorHandler({
                errorMessage: 'Failed to fetch labels',
                eventId: EventIds.LabelSuggestionsError,
                error,
            });
        } finally {
            isLoading = false;
        }
    }, []);

    return {
        load,
        labels,
        hasError,
        isLoading,
    };
};
